import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import {
  ArrowUpIcon,
  ClockIcon,
  InboxArrowDownIcon,
  LinkIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { useDrive, type DriveView } from '@/hooks/useDrive';
import { usePanelResize } from '@/hooks/usePanelResize';
import { PanelResizeHandle } from '@/components/PanelResizeHandle';
import { DriveFolderTree } from '@/components/drive/DriveFolderTree';
import { DriveFileList } from '@/components/drive/DriveFileList';
import { DriveToolbar } from '@/components/drive/DriveToolbar';
import { DriveDetailsPanel } from '@/components/drive/DriveDetailsPanel';
import { DriveTrashList } from '@/components/drive/DriveTrashList';
import { DrivePreviewModal } from '@/components/drive/DrivePreviewModal';
import { DriveShareDialog } from '@/components/drive/DriveShareDialog';
import { DriveVersionsDialog } from '@/components/drive/DriveVersionsDialog';
import { DriveContextMenu, driveContextItems } from '@/components/drive/DriveContextMenu';
import { UploadProgressPanel } from '@/components/drive/UploadProgressPanel';
import { StorageMeter } from '@/components/drive/StorageMeter';
import { ActivityView, MySharesView, SharedWithMeView } from '@/components/drive/DriveViews';
import { DriveRequestsView } from '@/components/drive/DriveRequests';
import { appsForFile, newInOfficeUrl, openInOfficeUrl, OFFICE_APPS, type OfficeAppId } from '@/lib/office/officeApps';
import type { DriveEntry } from '@/lib/drive/driveClient';

const ROOT = '/';

/** The dashboard's sub-views: the file browser, the trash, sharing and activity. */
type PageView = 'files' | 'trash' | 'shared' | 'shares' | 'activity' | 'requests';

function viewOf(raw: string | null): PageView {
  if (raw === 'trash' || raw === 'shared' || raw === 'shares' || raw === 'activity' || raw === 'requests') return raw;
  return 'files';
}

/**
 * The drive surface — sak's own, cloud-specific file browser over the
 * path-addressed drive client (bak's adapter architecture; see
 * docs/architecture/bak-single-file-ui.md). Files view plus a trash view in
 * the URL, upload via drag-and-drop, and live refresh through the hub socket.
 */
export function DrivePage() {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const pageView = viewOf(searchParams.get('view'));
  const view: DriveView = pageView === 'trash' ? 'trash' : 'files';
  const drive = useDrive(view);
  const locale = i18n.resolvedLanguage;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const folderPanel = usePanelResize({
    label: t('panels.resizeFolders'),
    storageKey: 'sak.panels.folderTreeWidth',
    defaultWidth: 240,
    minWidth: 200,
    maxWidth: 420,
    dock: 'left',
  });
  const detailsPanel = usePanelResize({
    label: t('panels.resizeDetails'),
    storageKey: 'sak.panels.detailsWidth',
    defaultWidth: 320,
    minWidth: 260,
    maxWidth: 520,
    dock: 'right',
  });

  // The trash view lives in the URL so the top bar's Trash entry lands on it.
  const toggleTrash = useCallback(() => {
    setSearchParams(view === 'files' ? { view: 'trash' } : {}, { replace: true });
  }, [view, setSearchParams]);

  const selected = drive.selectedEntry ?? drive.selectedTrash;

  // The suite top bar's Drive section reaches this page through window events.
  useEffect(() => {
    const onUpload = () => fileInputRef.current?.click();
    const onNewFolder = () => void drive.runAction('newFolder');
    window.addEventListener('sak:upload', onUpload);
    window.addEventListener('sak:new-folder', onNewFolder);
    return () => {
      window.removeEventListener('sak:upload', onUpload);
      window.removeEventListener('sak:new-folder', onNewFolder);
    };
  }, [drive.runAction]);

  // Keyboard shortcuts: the drive's muscle-memory set (Select all, Delete, rename,
  // clipboard, Escape). Only in the files view — the trash never deletes.
  useEffect(() => {
    if (pageView !== 'files') return;
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return;
      const mod = event.ctrlKey || event.metaKey;
      if (mod && event.key.toLowerCase() === 'a') {
        event.preventDefault();
        drive.selectAll();
      } else if (mod && event.key.toLowerCase() === 'c' && drive.selectedKey) {
        void drive.runAction('copy');
      } else if (mod && event.key.toLowerCase() === 'x' && drive.selectedKey) {
        void drive.runAction('cut');
      } else if (mod && event.key.toLowerCase() === 'v' && drive.clipboard) {
        void drive.runAction('paste');
      } else if (event.key === 'Delete' && drive.selectedKey) {
        event.preventDefault();
        void drive.runAction('delete');
      } else if (event.key === 'F2' && drive.selectedKey) {
        event.preventDefault();
        void drive.runAction('rename');
      } else if (event.key === 'Escape') {
        drive.clearSelection();
        drive.setContextMenu(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pageView, drive]);

  const toolbarAction = useCallback(
    (action: string) => {
      if (action === 'upload') {
        fileInputRef.current?.click();
        return;
      }
      if (action === 'uploadFolder') {
        folderInputRef.current?.click();
        return;
      }
      void drive.runAction(action);
    },
    [drive.runAction],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    noClick: true,
    disabled: view === 'trash',
    onDrop: (accepted) => {
      void drive.uploadFiles(accepted);
    },
  });

  const breadcrumb = useMemo(
    () => (drive.currentPath === ROOT ? t('drive.myFiles') : drive.currentPath),
    [drive.currentPath, t],
  );

  // Double-click semantics: folders open, files preview — the viewer is the drive's "open".
  const handleOpen = useCallback(
    (entry: DriveEntry) => {
      if (entry.kind === 'folder') void drive.runAction('open', entry);
      else void drive.runAction('preview', entry);
    },
    [drive.runAction],
  );

  // New office document: a deep link starts a blank doc bound to a name in the office app;
  // the app's bridge saves it back into this folder as a new file.
  const handleNewOffice = useCallback(
    (appId: OfficeAppId) => {
      const app = OFFICE_APPS.find((candidate) => candidate.id === appId);
      if (!app) return;
      const seededName = `Untitled.${app.newExtension}`;
      const url = newInOfficeUrl(app, seededName);
      if (url) window.open(url, '_blank', 'noopener');
    },
    [],
  );

  const officeMenuItems = useMemo(() => {
    const entry = drive.selectedEntry;
    if (!entry || entry.kind !== 'file') return [];
    return appsForFile(entry.name).map((app, index) => ({
      action: `openIn:${app.id}`,
      label: t('drive.openIn', { app: app.label }),
      separatorBefore: index === 0,
    }));
  }, [drive.selectedEntry, t]);

  return (
    <div className="flex min-h-0 w-full flex-1 gap-0">
      <aside
        className="hidden flex-col gap-3 py-4 ps-4 lg:flex"
        style={{ width: folderPanel.width }}
        aria-label={t('drive.myFiles')}
      >
        <DriveFolderTree
          tree={drive.tree}
          selectedPath={drive.currentPath}
          onSelect={drive.openFolder}
          onDropOnFolder={(folderPath) => {
            const dragged = drive.selectedEntry;
            if (dragged) void drive.runAction('moveTo', dragged, { folderPath });
          }}
        />
        <StorageMeter />
      </aside>
      <PanelResizeHandle state={folderPanel} variant="ghost" className="hidden lg:block" />

      <div className="relative flex min-w-0 flex-1 flex-col gap-4 p-4" {...getRootProps()}>
        <input {...getInputProps()} />
        {/* Two doors the toolbar and the suite top bar press: plain upload and folder upload. */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          hidden
          onChange={(event) => {
            const files = Array.from(event.target.files ?? []);
            event.target.value = '';
            void drive.uploadFiles(files);
          }}
        />
        {/* Mobile camera upload: the phone's camera app feeds the drive directly. */}
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*,video/*"
          capture="environment"
          multiple
          hidden
          onChange={(event) => {
            const files = Array.from(event.target.files ?? []);
            event.target.value = '';
            void drive.uploadFiles(files);
          }}
        />
        <input
          ref={folderInputRef}
          type="file"
          multiple
          hidden
          {...({ webkitdirectory: 'true', directory: 'true' } as Record<string, string>)}
          onChange={(event) => {
            const files = Array.from(event.target.files ?? []);
            event.target.value = '';
            void drive.uploadFolderFiles(files);
          }}
        />

        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="min-w-0 truncate text-xl font-semibold text-foreground" title={breadcrumb}>
            {pageView === 'trash'
              ? t('navigation.trash')
              : pageView === 'shared'
                ? t('drive.sharedWithMe')
                : pageView === 'shares'
                  ? t('drive.myShares')
                  : pageView === 'activity'
                    ? t('drive.activity')
                    : breadcrumb}
          </h1>
          <div className="flex flex-shrink-0 flex-nowrap items-center gap-1 overflow-x-auto">
            {pageView === 'files' && drive.currentPath !== ROOT && (
              <button
                type="button"
                onClick={() =>
                  drive.openFolder(
                    drive.currentPath.slice(0, drive.currentPath.lastIndexOf('/')) || ROOT,
                  )
                }
                className="flex items-center gap-1 whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium text-foreground transition-colors hover:bg-muted"
              >
                <ArrowUpIcon className="h-4 w-4 flex-shrink-0" />
                {t('drive.up')}
              </button>
            )}
            <NavChip
              active={pageView === 'shared'}
              onClick={() => setSearchParams(pageView === 'shared' ? {} : { view: 'shared' }, { replace: true })}
              Icon={UserGroupIcon}
              label={t('drive.sharedWithMe')}
            />
            <NavChip
              active={pageView === 'shares'}
              onClick={() => setSearchParams(pageView === 'shares' ? {} : { view: 'shares' }, { replace: true })}
              Icon={LinkIcon}
              label={t('drive.myShares')}
            />
            <NavChip
              active={pageView === 'activity'}
              onClick={() => setSearchParams(pageView === 'activity' ? {} : { view: 'activity' }, { replace: true })}
              Icon={ClockIcon}
              label={t('drive.activity')}
            />
            {pageView === 'files' && (
              <button
                type="button"
                onClick={() => cameraInputRef.current?.click()}
                className="whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium text-foreground transition-colors hover:bg-muted md:hidden"
              >
                {t('drive.cameraUpload')}
              </button>
            )}
            <NavChip
              active={pageView === 'requests'}
              onClick={() => setSearchParams(pageView === 'requests' ? {} : { view: 'requests' }, { replace: true })}
              Icon={InboxArrowDownIcon}
              label={t('drive.fileRequests')}
            />
            <NavChip
              active={pageView === 'trash'}
              onClick={toggleTrash}
              Icon={null}
              label={pageView === 'trash' ? t('drive.backToFiles') : t('drive.openTrash')}
            />
          </div>
        </div>

        {pageView !== 'activity' && pageView !== 'shared' && pageView !== 'shares' && pageView !== 'requests' && (
          <DriveToolbar
            isTrashView={pageView === 'trash'}
            viewMode={drive.viewMode}
            sortBy={drive.sortBy}
            sortAscending={drive.sortAscending}
            hasSelection={!!drive.selectedKey}
            hasClipboard={!!drive.clipboard}
            showDetails={drive.detailsOpen}
            searchQuery={drive.searchQuery}
            searching={drive.searching}
            onViewModeChange={drive.setViewMode}
            onSortByChange={drive.setSortBy}
            onSortDirectionToggle={() => drive.setSortAscending(!drive.sortAscending)}
            onToggleDetails={() => drive.setDetailsOpen(!drive.detailsOpen)}
            onSearchChange={(query) => void drive.runSearch(query)}
            onAction={toolbarAction}
            onNewOffice={handleNewOffice}
          />
        )}

        <div
          className={clsx(
            'rounded-lg border-2 border-dashed px-4 py-2.5 text-center transition-colors',
            isDragActive ? 'border-accent bg-active' : 'border-border-color',
            drive.uploading && 'animate-pulse',
          )}
        >
          <p className="whitespace-nowrap text-sm text-muted-foreground">
            {drive.uploading
              ? t('drive.uploading')
              : pageView === 'trash'
                ? t('drive.trashCount', { count: drive.trash.length })
                : pageView === 'files' && drive.searchResults
                  ? t('drive.searchCount', { count: drive.searchResults.length })
                  : t('drive.dropToUpload')}
          </p>
        </div>

        <div className="flex min-h-0 flex-1 flex-col">
          {drive.loading && pageView === 'files' ? (
            <div className="flex min-h-64 flex-1 items-center justify-center rounded-lg border border-border-color bg-card">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-border-color border-t-accent" />
            </div>
          ) : pageView === 'trash' ? (
            <DriveTrashList
              rows={drive.trash}
              selectedId={drive.selectedTrash?.id ?? null}
              locale={locale}
              onSelect={(id) => drive.setSelectedKey(id)}
            />
          ) : pageView === 'shared' ? (
            <SharedWithMeView />
          ) : pageView === 'shares' ? (
            <MySharesView />
          ) : pageView === 'activity' ? (
            <ActivityView />
          ) : pageView === 'requests' ? (
            <DriveRequestsView />
          ) : (
            <DriveFileList
              entries={drive.searchResults ?? drive.entries}
              viewMode={drive.viewMode}
              selectedPaths={drive.searchResults ? new Set() : drive.selectedPaths}
              locale={locale}
              onSelect={(entry) => drive.selectEntry(entry)}
              onSelectModifiers={(entry, event) =>
                drive.selectEntry(entry, {
                  multi: event.ctrlKey || event.metaKey,
                  range: event.shiftKey,
                })
              }
              onOpen={handleOpen}
              onContextMenu={(entry, x, y) => {
                if (!drive.selectedPaths.has(entry.path)) drive.selectEntry(entry);
                drive.setContextMenu({ x, y });
              }}
              onDropOnFolder={(folder, dragged) => void drive.runAction('moveTo', dragged, { folderPath: folder.path })}
            />
          )}
        </div>

        <UploadProgressPanel
          tasks={drive.uploadTasks}
          onCancel={drive.cancelUpload}
          onClear={drive.clearUploads}
        />

        <DriveContextMenu
          state={drive.contextMenu}
          items={[...driveContextItems(!!drive.selectedKey, t), ...officeMenuItems]}
          onAction={(action) => {
            if (action.startsWith('openIn:')) {
              const entry = drive.selectedEntry;
              const app = OFFICE_APPS.find((candidate) => candidate.id === action.slice('openIn:'.length));
              if (entry && app) {
                const url = openInOfficeUrl(app, entry.id, entry.name);
                if (url) window.open(url, '_blank', 'noopener');
              }
              return;
            }
            if (action === 'open' || action === 'preview') {
              const entry = drive.selectedEntry;
              if (entry?.kind === 'folder') void drive.runAction('open', entry);
              else if (entry) void drive.runAction('preview', entry);
              return;
            }
            void drive.runAction(action);
          }}
          onClose={() => drive.setContextMenu(null)}
        />

        <DrivePreviewModal
          entry={drive.previewEntry}
          onClose={() => drive.setPreviewEntry(null)}
          onDownload={(entry) => void drive.runAction('download', entry)}
        />
        <DriveShareDialog
          entry={drive.shareEntry}
          onClose={() => drive.setShareEntry(null)}
        />
        <DriveVersionsDialog
          entry={drive.versionsEntry}
          onClose={() => drive.setVersionsEntry(null)}
          onRestored={() => void drive.refresh()}
        />
      </div>

      {drive.detailsOpen && (
        <>
          <PanelResizeHandle state={detailsPanel} variant="separator" />
          <div className="flex min-h-0 flex-col py-4 pe-4" style={{ width: detailsPanel.width }}>
            <DriveDetailsPanel
              entry={selected}
              pathLabel={drive.currentPath}
              locale={locale}
              onClose={() => drive.setDetailsOpen(false)}
            />
          </div>
        </>
      )}
    </div>
  );
}

/** One of the header's view chips — Files/Trash/Shared/Shares/Activity navigation. */
function NavChip({
  active,
  onClick,
  Icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>> | null;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={clsx(
        'flex items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
        active ? 'bg-active text-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground',
      )}
    >
      {Icon && <Icon className="h-4 w-4 flex-shrink-0" />}
      <span className="whitespace-nowrap">{label}</span>
    </button>
  );
}
